import type { DataSource, Entity, FlightEntity, MapBounds } from '../types/entity';

/**
 * OpenSky Network — estado global de voos ADS-B, sem autenticação
 * necessária no endpoint anônimo (rate limit mais apertado que o
 * autenticado, mas suficiente pra refresh de dashboard).
 * Docs: https://openskynetwork.github.io/opensky-api/rest.html
 */

// Tupla posicional documentada pela API — índices fixos, sem nomes.
type OpenSkyStateVector = [
  icao24: string,
  callsign: string | null,
  originCountry: string,
  timePosition: number | null,
  lastContact: number,
  longitude: number | null,
  latitude: number | null,
  baroAltitude: number | null,
  onGround: boolean,
  velocity: number | null,
  trueTrack: number | null,
  verticalRate: number | null,
  sensors: number[] | null,
  geoAltitude: number | null,
  squawk: string | null,
  spi: boolean,
  positionSource: number,
];

interface OpenSkyResponse {
  time: number;
  states: OpenSkyStateVector[] | null;
}

const OPENSKY_ENDPOINT = 'https://opensky-network.org/api/states/all';

function toFlightEntity(state: OpenSkyStateVector, observedAt: string): FlightEntity | null {
  const [icao24, callsign, originCountry, , , lon, lat, baroAltitude, onGround, velocity, heading] = state;

  if (lat === null || lon === null) return null;

  return {
    id: `flight-${icao24}`,
    layer: 'flights',
    lat,
    lon,
    label: callsign?.trim() || icao24.toUpperCase(),
    severity: 'normal',
    observedAt,
    raw: {
      callsign: callsign?.trim() || null,
      originCountry,
      altitudeM: baroAltitude,
      velocityMs: velocity,
      headingDeg: heading,
      onGround,
    },
  };
}

export const openSkySource: DataSource = {
  layer: 'flights',
  label: 'Voos (OpenSky)',
  requiresClientKey: false,
  viaCloudFunction: false,

  async fetchEntities(bounds?: MapBounds): Promise<Entity[]> {
    const url = new URL(OPENSKY_ENDPOINT);

    // Sem bounds, a API retorna o estado global inteiro (milhares de
    // aeronaves) — sempre restringir à área visível do mapa.
    if (bounds) {
      url.searchParams.set('lamin', bounds.south.toFixed(4));
      url.searchParams.set('lamax', bounds.north.toFixed(4));
      url.searchParams.set('lomin', bounds.west.toFixed(4));
      url.searchParams.set('lomax', bounds.east.toFixed(4));
    }

    const res = await fetch(url.toString());
    if (!res.ok) {
      throw new Error(`OpenSky respondeu ${res.status}`);
    }

    const data = (await res.json()) as OpenSkyResponse;
    if (!data.states) return [];

    const observedAt = new Date(data.time * 1000).toISOString();
    const entities: Entity[] = [];

    for (const state of data.states) {
      const entity = toFlightEntity(state, observedAt);
      if (entity) entities.push(entity);
    }

    return entities;
  },
};
